import questionsHandler from "../questionsHandler";
import {READING} from "@/constants";

export class ConditionsDTO {

  constructor(configuration, context, readingId) {
    this.configuration = configuration
    this.readingId = this.readingId === null ? 'default' : readingId
    this.context = context

    this.attachmentListToParseForConditions = {}
    this.focused = false
    this.screensToInsert = new Map()
    this.groupsToInsert = new Map()
    this.questionsToInsert = new Map()
    this.visibleQuestionsPerScreen = new Map()
    this.visibleQuestionsPerGroup = new Map()
    this.screensWithAttachmentsList = {}
    this.invisibleScreens = new Map()
    this.invisibleGroups = new Map()
    this.invisibleQuestions = new Map()
    this.conditionsSetInAdvanceForQuestionIds = new Set()
    this.answerValuePerCode = this.generateAnswerValuePerCode()
    this.associativeGroup = this.configuration?.group.reduce((acc, obj) => {
      if (obj.active) {
        acc[obj.id] = obj
      }
      return acc
    }, {})
    this.associativeScreen = this.configuration?.screen.reduce((acc, obj) => {
      if (obj.active) {
        acc[obj.id] = obj
      }
      return acc
    }, {})
  }

  generateAnswerValuePerCode() {
    const answerValuePerCode = {}
    this.context?.answers?.forEach((answer) => {
      let code = answer.question.toString()
      if (answer.attachment_id) {
        code += `:${answer.attachment_type}|${answer.attachment_id}`
      }
      answerValuePerCode[code] = answer.value
    })
    return answerValuePerCode
  }

  addScreenToScreenWithAttachmentsList(screenId, attachmentType, attachmentIds) {
    this.screensWithAttachmentsList[screenId] = {
      attachmentType: attachmentType,
      attachmentIds: attachmentIds
    }
  }

  fillScreensWithAttachmentsListWithDummyItems(currentAttachmentList, screenId) {
    if (currentAttachmentList.minimumRequired > 0) {
      const nbAttachmentsLeftToCompleteMinimumRequired =
        currentAttachmentList.minimumRequired - this.screensWithAttachmentsList[screenId].attachmentIds.length
      if (nbAttachmentsLeftToCompleteMinimumRequired > 0) {
        for (let i = 0; i < nbAttachmentsLeftToCompleteMinimumRequired; i++) {
          this.screensWithAttachmentsList[screenId].attachmentIds.push('dummy_item_' + i)
        }
      }
    }
  }
  createScreenAttachments() {
    if (!this.configuration.hasOwnProperty('screen')) {
      return
    }
    const screens = Object.values(this.configuration.screen).filter((screen) => screen.active)
    screens.forEach(
      function (screen) {
        const screenId = screen.id
        if (this.attachmentListToParseForConditions.hasOwnProperty(screen.attachment?.value)) {
          const currentAttachmentList = this.attachmentListToParseForConditions[screen.attachment.value]
          const attachmentIds = currentAttachmentList.attachmentIds
          this.addScreenToScreenWithAttachmentsList(screenId, screen.attachment.value, attachmentIds)
          this.fillScreensWithAttachmentsListWithDummyItems(currentAttachmentList, screenId)
        }
      }.bind(this)
    )
  }

  removeInvisibleQuestionsFromVisibleQuestionsPerGroup(questionReadingConditions) {
    this.visibleQuestionsPerGroup.forEach(function (questionIds, groupKey) {
      questionIds.forEach(function (questionId) {
        if (!questionReadingConditions[questionId]?.visibility) {
          questionIds.delete(questionId)
        }
      })
    })
  }

  removeInvisibleQuestionsFromVisibleQuestionsPerScreen(questionReadingConditions) {
    this.visibleQuestionsPerScreen.forEach(function (questionIds, screenId) {
      questionIds.forEach(function (questionId) {
        if (!questionReadingConditions[questionId]?.visibility) {
          questionIds.delete(questionId)
        }
      })
    })
  }

  addInvisibleScreenToInvisibleList(conditionResult, conditionType, screenId) {
    if (!conditionResult && conditionType === 'visibility') {
      this.invisibleScreens.set(screenId, true)
    }
  }

  addInvisibleGroupToInvisibleList(
    conditionResult,
    conditionType,
    groupId,
    attachmentType = 'none',
    attachmentId = null
  ) {
    if (!conditionResult && conditionType === 'visibility') {
      const builtId = questionsHandler.buildId(groupId, attachmentType, attachmentId)
      this.invisibleGroups.set(builtId, true)
    }
  }

  screenHasAttachments(screenId) {
    return screenId !== null && this.screensWithAttachmentsList.hasOwnProperty(screenId)
  }

  addQuestionToVisibleQuestionsPerScreen(screenId, questionId) {
    const currentScreenMap = this.visibleQuestionsPerScreen.get(screenId?.toString()) || new Set()
    currentScreenMap.add(questionId.toString())
    this.visibleQuestionsPerScreen.set(screenId?.toString(), currentScreenMap)
  }

  removeQuestionFromVisibleQuestionsPerScreen(screenId, questionId) {
    const currentScreenMap = this.visibleQuestionsPerScreen.get(screenId?.toString()) || new Set()
    currentScreenMap.delete(questionId.toString())
    this.visibleQuestionsPerScreen.set(screenId?.toString(), currentScreenMap)
  }

  addQuestionToVisibleQuestionsPerGroup(groupId, questionId) {
    const currentGroupMap = this.visibleQuestionsPerGroup.get(groupId?.toString()) || new Set()
    currentGroupMap.add(questionId.toString())
    this.visibleQuestionsPerGroup.set(groupId?.toString(), currentGroupMap)
  }

  removeQuestionFromVisibleQuestionsPerGroup(groupId, questionId) {
    const currentGroupMap = this.visibleQuestionsPerGroup.get(groupId?.toString()) || new Set()
    currentGroupMap.delete(questionId.toString())
    this.visibleQuestionsPerGroup.set(groupId?.toString(), currentGroupMap)
  }

  questionBelongsToInvisibleGroup(groupId, attachmentType = 'none', attachmentId = null) {
    const builtId = questionsHandler.buildId(groupId, attachmentType, attachmentId)
    return this.invisibleGroups.has(builtId)
  }

  addOrRemoveInvisibleQuestionFromInvisibleList(
    conditionResult,
    conditionType,
    questionId,
    attachmentType = 'none',
    attachmentId = null
  ) {
    if (conditionType !== 'visibility') {
      return
    }
    const builtId = questionsHandler.buildId(questionId, attachmentType, attachmentId)
    if (!conditionResult) {
      this.invisibleQuestions.set(builtId, true)
    }
    else {
      this.invisibleQuestions.delete(builtId)
    }
  }

  setProdContext(attachmentType, attachmentId) {
    if (attachmentType === READING.ATTACHMENT_TYPE.PRODUCT) {
      return this.configuration.catalog.product[attachmentId]
    }
    return null
  }

  groupBelongsToInvisibleScreen(screenId) {
    return this.invisibleScreens.has(screenId)
  }

  screenHasVisibleQuestions(screenId) {
    return (
      this.visibleQuestionsPerScreen.has(screenId.toString()) &&
      this.visibleQuestionsPerScreen.get(screenId.toString()).size > 0
    )
  }
  groupHasVisibleQuestions(groupId) {
    return (
      this.visibleQuestionsPerGroup.has(groupId.toString()) &&
      this.visibleQuestionsPerGroup.get(groupId.toString()).size > 0
    )
  }

  updateEmptyScreenVisibility() {
    if (this.configuration.hasOwnProperty('screen')) {
      const screens = Object.values(this.configuration.screen).filter((screen) => screen.active)
      screens.forEach(
        function (screen) {
          const screenId = screen.id
          const canShowScreen = this.screenHasVisibleQuestions(screenId) ||
            [READING.ATTACHMENT_TYPE.PICTURE, READING.ATTACHMENT_TYPE.ELEMENT].includes(screen.attachment?.value)
          if (!canShowScreen) {
            const currentScreenMap = this.screensToInsert.get(screenId.toString()) || new Map()
            currentScreenMap.set('visibility', false)
            this.screensToInsert.set(screenId.toString(), currentScreenMap)
          }
        }.bind(this)
      )
    }
  }

  updateEmptyGroupVisibility() {
    if (this.configuration.hasOwnProperty('group')) {
      const groups = Object.values(this.configuration.group).filter((group) => group.active)
      for (const group of groups) {
        const groupId = group.id
        if (this.configuration.hasOwnProperty('screen')) {
          const groupScreen = this.associativeScreen?.[group?.screen_id]
          if (!this.attachmentListToParseForConditions.hasOwnProperty(groupScreen?.attachment?.value)) {
            continue
          }
          const attachmentIds = this.attachmentListToParseForConditions[groupScreen?.attachment.value].attachmentIds
          for (const attachmentId of attachmentIds) {
            const groupBuiltId = questionsHandler.buildId(groupId, groupScreen?.attachment.value, attachmentId)
            if (!this.groupHasVisibleQuestions(groupBuiltId)) {
              const itemBuiltId = questionsHandler.buildId(groupId, groupScreen?.attachment.value, attachmentId)
              const currentGroupMap = this.groupsToInsert.get(itemBuiltId) || new Map()
              currentGroupMap.set('visibility', false)
              this.groupsToInsert.set(itemBuiltId, currentGroupMap)
            }
          }

        }
        if (!this.groupHasVisibleQuestions(groupId)) {
          const currentGroupMap = this.groupsToInsert.get(groupId.toString()) || new Map()
          currentGroupMap.set('visibility', false)
          this.groupsToInsert.set(groupId.toString(), currentGroupMap)
        }
      }
    }
  }
}